<template>
  <section class="info-page">
    <PageTitleBar
      class="info-page-title"
      title-id="info-page-title"
      title="Help MyStudio"
      subtitle="Guide, tutorial e risorse per utilizzare al meglio MyStudio."
      icon-class="fa-solid fa-circle-question"
      illustration-src="/Pagina Info/Finestra con lampadina.png"
      variant="info"
    />
    <div class="info-layout">
      <aside class="info-navigation" aria-label="Categorie Help MyStudio">
        <h2>Categorie</h2>
        <nav class="info-navigation-list">
          <button
            v-for="section in sections"
            :key="section.id"
            type="button"
            class="info-navigation-item"
            :class="{ active: activeSectionId === section.id }"
            @click="selectSection(section.id)"
          >
            <span class="info-navigation-icon" aria-hidden="true">
              <ProductIcon
                v-if="section.product"
                :product="section.product"
                class="info-navigation-product-icon"
              />
              <img
                v-else-if="section.iconImage"
                :src="section.iconImage"
                class="info-navigation-logo"
                alt=""
              />
              <i v-else :class="section.icon"></i>
            </span>
            <span>{{ section.title }}</span>
            <i
              v-if="activeSectionId === section.id"
              class="fa-solid fa-chevron-right navigation-chevron"
              aria-hidden="true"
            ></i>
          </button>
        </nav>

        <div class="info-navigation-help">
          <span class="help-icon" aria-hidden="true">
            <i class="fa-solid fa-question"></i>
          </span>
          <div>
            <strong>Serve aiuto?</strong>
            <p>
              Naviga tra le sezioni per trovare tutte le risorse disponibili.
            </p>
          </div>
        </div>
      </aside>

      <main
        class="info-content"
        :class="
          activeSection.product
            ? `info-content--${activeSection.product}`
            : null
        "
      >
        <header
          class="section-hero"
          :class="{ 'section-hero--intro': isIntroSection }"
        >
          <span class="section-hero-icon" aria-hidden="true">
            <ProductIcon
              v-if="activeSection.product"
              :product="activeSection.product"
            />
            <img
              v-else-if="activeSection.iconImage"
              :src="activeSection.iconImage"
              alt=""
            />
            <i v-else :class="activeSection.icon"></i>
          </span>
          <div class="section-hero-copy">
            <h2>{{ activeSection.title }}</h2>
            <p>{{ activeSection.description }}</p>
          </div>
          <div
            v-if="isIntroSection"
            class="intro-resource-switcher"
            aria-label="Risorse introduttive"
          >
            <button
              v-for="resource in introResources"
              :key="resource.type"
              type="button"
              class="resource-card intro-resource-card"
              :class="{
                active:
                  activeResource &&
                  activeResource.key === resourceKey(introTopic, resource),
              }"
              @click="toggleResource(introTopic, resource)"
            >
              <span class="resource-icon" aria-hidden="true">
                <i :class="resource.icon"></i>
              </span>
              <span class="resource-copy">
                <strong>{{ resource.title }}</strong>
                <small>{{ resource.description }}</small>
              </span>
              <i
                :class="
                  activeResource &&
                  activeResource.key === resourceKey(introTopic, resource)
                    ? 'fa-solid fa-chevron-down'
                    : 'fa-solid fa-chevron-right'
                "
                class="resource-chevron"
                aria-hidden="true"
              ></i>
            </button>
          </div>
          <img
            v-if="activeSection.heroImage"
            class="section-hero-illustration"
            :class="activeSection.heroImageClass"
            :src="activeSection.heroImage"
            alt=""
            aria-hidden="true"
          />
        </header>

        <template v-if="activeSectionHasResources">
          <div v-if="!isIntroSection" class="info-controls">
            <label class="info-search">
              <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              <span class="sr-only">Cerca nella sezione</span>
              <input
                v-model.trim="searchQuery"
                type="search"
                placeholder="Cerca in questa sezione..."
              />
            </label>

            <div class="info-filters" aria-label="Filtra le risorse">
              <button
                type="button"
                :class="{ active: activeFilter === 'all' }"
                @click="setFilter('all')"
              >
                Tutto ({{ resourceCounts.all }})
              </button>
              <button
                type="button"
                :class="{ active: activeFilter === 'documentation' }"
                @click="setFilter('documentation')"
              >
                <i class="fa-solid fa-file-lines" aria-hidden="true"></i>
                Documentazione ({{ resourceCounts.documentation }})
              </button>
              <button
                type="button"
                :class="{ active: activeFilter === 'tutorial' }"
                @click="setFilter('tutorial')"
              >
                <i class="fa-solid fa-video" aria-hidden="true"></i>
                Tutorial ({{ resourceCounts.tutorial }})
              </button>
            </div>
          </div>

          <div
            class="info-results"
            :class="{ 'info-results--intro': isIntroSection }"
          >
            <div v-if="filteredTopics.length" class="topic-list">
              <article
                v-for="topic in filteredTopics"
                :key="topic.title"
                class="topic-card"
                :class="{ 'topic-card--intro': isIntroSection }"
              >
                <div v-if="!isIntroSection" class="topic-heading">
                  <span class="topic-icon" aria-hidden="true">
                    <LibraryIcon
                      v-if="topic.library"
                      :library="topic.library"
                    />
                    <ProductIcon
                      v-else-if="topic.product"
                      :product="topic.product"
                    />
                    <i v-else :class="topic.icon"></i>
                  </span>
                  <div>
                    <h3>{{ topic.title }}</h3>
                    <p>{{ topic.description }}</p>
                  </div>
                </div>

                <div v-if="!isIntroSection" class="topic-resources">
                  <button
                    v-for="resource in topic.filteredResources"
                    :key="resource.type"
                    type="button"
                    class="resource-card"
                    :class="{
                      active:
                        activeResource &&
                        activeResource.key === resourceKey(topic, resource),
                    }"
                    @click="toggleResource(topic, resource)"
                  >
                    <span class="resource-icon" aria-hidden="true">
                      <i :class="resource.icon"></i>
                    </span>
                    <span class="resource-copy">
                      <strong>{{ resource.title }}</strong>
                      <small>{{ resource.description }}</small>
                    </span>
                    <i
                      :class="
                        activeResource &&
                        activeResource.key === resourceKey(topic, resource)
                          ? 'fa-solid fa-chevron-up'
                          : 'fa-solid fa-chevron-right'
                      "
                      class="resource-chevron"
                      aria-hidden="true"
                    ></i>
                  </button>
                </div>

                <div
                  v-if="
                    activeResource && activeResource.topicTitle === topic.title
                  "
                  class="resource-viewer"
                  :class="{ 'resource-viewer--intro': isIntroSection }"
                >
                  <div
                    v-if="activeResource.type !== 'documentation'"
                    class="resource-viewer-heading"
                  >
                    <div>
                      <span>{{ activeResource.title }}</span>
                      <strong>{{ topic.title }}</strong>
                    </div>
                    <button
                      v-if="!isIntroSection"
                      type="button"
                      title="Chiudi risorsa"
                      aria-label="Chiudi risorsa"
                      @click="closeResource"
                    >
                      <i class="fa-solid fa-xmark"></i>
                    </button>
                  </div>

                  <HelpPdfViewer
                    v-if="activeResource.type === 'documentation'"
                    :src="`${API_BASE_URL}${activeResource.src}`"
                    class="resource-pdf"
                    :title="activeResource.title"
                    :subtitle="topic.title"
                    :closable="!isIntroSection"
                    @close="closeResource"
                  />

                  <video
                    v-else-if="isIntroSection"
                    controls
                    controlsList="nodownload noremoteplayback"
                    disableRemotePlayback
                    preload="metadata"
                    class="tutorial-video"
                    :src="`${API_BASE_URL}${activeResource.videos[0].src}`"
                    @contextmenu.prevent
                  ></video>

                  <div v-else class="tutorial-list">
                    <article
                      v-for="(video, videoIndex) in activeResource.videos"
                      :key="video.src"
                      class="tutorial-item"
                    >
                      <button
                        type="button"
                        class="tutorial-trigger"
                        @click="toggleVideo(videoIndex)"
                      >
                        <span aria-hidden="true">
                          <i class="fa-solid fa-circle-play"></i>
                        </span>
                        <strong>{{ video.text }}</strong>
                        <i
                          :class="
                            activeVideoIndex === videoIndex
                              ? 'fa-solid fa-chevron-up'
                              : 'fa-solid fa-chevron-down'
                          "
                          aria-hidden="true"
                        ></i>
                      </button>
                      <video
                        v-if="activeVideoIndex === videoIndex"
                        controls
                        controlsList="nodownload noremoteplayback"
                        disableRemotePlayback
                        class="tutorial-video"
                        @contextmenu.prevent
                      >
                        <source
                          :src="`${API_BASE_URL}${video.src}`"
                          type="video/mp4"
                        />
                        Il tuo browser non supporta il video HTML5.
                      </video>
                    </article>
                  </div>
                </div>
              </article>
            </div>

            <div v-else class="no-search-results">
              <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              <p>Nessuna risorsa corrisponde alla ricerca.</p>
            </div>
          </div>
        </template>

        <div v-else class="empty-section-message" role="status">
          <span aria-hidden="true">
            <i class="fa-regular fa-file-lines"></i>
          </span>
          <p>Documentazione presto in arrivo</p>
        </div>
      </main>
    </div>
  </section>
</template>

<script>
import { API_BASE_URL } from "@/config.js";
import HelpPdfViewer from "@/components/HelpPdfViewer.vue";
import LibraryIcon from "@/components/LibraryIcon.vue";
import PageTitleBar from "@/components/PageTitleBar.vue";
import ProductIcon from "@/components/ProductIcon.vue";

const pdfResource = (src, description = "Scarica la guida completa") => ({
  type: "documentation",
  title: "Presentazione PDF",
  description,
  icon: "fa-solid fa-file-pdf",
  src,
});

const videoResource = (
  videos,
  description = "Guarda i tutorial passo-passo"
) => ({
  type: "tutorial",
  title: "Video Tutorial",
  description,
  icon: "fa-solid fa-video",
  videos,
});

export default {
  name: "HelpMyStudio",
  components: {
    HelpPdfViewer,
    LibraryIcon,
    PageTitleBar,
    ProductIcon,
  },
  data() {
    return {
      API_BASE_URL,
      activeSectionId: "intro",
      activeFilter: "all",
      searchQuery: "",
      activeResource: null,
      activeVideoIndex: null,
      sections: [
        {
          id: "intro",
          title: "Intro MyStudio",
          icon: "fa-solid fa-house",
          iconImage: "/Pagina%20Welcome/logo.png",
          description:
            "Scopri l'interfaccia e le funzionalità principali di MyStudio.",
          heroImage: "",
          topics: [
            {
              title: "Intro MyStudio",
              description: "",
              icon: "fa-solid fa-display",
              resources: [
                pdfResource(
                  "/static/pdf/Help MyStudio - Presentazione.pdf",
                  "Apri l'introduzione alla Piattaforma MyStudio"
                ),
                videoResource([
                  {
                    src: "/static/video/Help MyStudio - Presentazione Video.mp4",
                    text: "Intro MyStudio",
                  },
                ]),
              ],
            },
          ],
        },
        {
          id: "prospetti",
          title: "Prospetti Paghe",
          product: "prospetti",
          description:
            "Gestisci la conversione dei prospetti paghe e il piano dei conti associato.",
          heroImage: "/Pagina Info/Documenti.png",
          topics: [
            {
              title: "Convertitore Prospetti Paghe",
              description:
                "Elabora file paghe in pochi click e importa automaticamente i dati nel tuo sistema contabile, riducendo errori e tempi di elaborazione.",
              product: "prospetti",
              resources: [
                pdfResource("/static/pdf/Convertitore Prospetti Paghe.pdf"),
                videoResource([
                  {
                    src: "/static/video/Conversione TS1.mp4",
                    text: "Come convertire un Prospetto Paghe passo per passo",
                  },
                  {
                    src: "/static/video/Conversione Prospetto Paghe da TS a DATEV.mp4",
                    text: "Come convertire un Prospetto Paghe nel Software Contabile DATEV",
                  },
                  {
                    src: "/static/video/Conversione TS - Caso nessun cod conto.mp4",
                    text: "Conversione con tutti i Codici Conto già presenti nel Piano dei Conti",
                  },
                  {
                    src: "/static/video/Conversione Prospetto Paghe con personalizzazione - Completa.mp4",
                    text: "Personalizzare nome e descrizione dei conti durante la conversione",
                  },
                ]),
              ],
            },
            {
              title: "Libreria Codici Conto",
              description:
                "Salva e riutilizza le voci contabili dai prospetti paghe convertiti, per una gestione ordinata e completa.",
              library: "conti",
              resources: [
                pdfResource("/static/pdf/Piano dei Conti.pdf"),
                videoResource([
                  {
                    src: "/static/video/Piano dei conti - Leggi Prospetto Paghe.mp4",
                    text: 'Aggiornare il Piano dei Conti con "Leggi Prospetto Paghe"',
                  },
                  {
                    src: "/static/video/Piano dei conti - Aggiungi Riga.mp4",
                    text: 'Aggiornare il Piano dei Conti con "Aggiungi Riga"',
                  },
                  {
                    src: "/static/video/Piano dei conti - Carica Piano.mp4",
                    text: 'Aggiornare il Piano dei Conti con "Carica Piano"',
                  },
                ]),
              ],
            },
          ],
        },
        {
          id: "f24",
          title: "Modelli F24",
          product: "f24",
          description:
            "Gestisci la conversione dei modelli F24 e il piano codici tributo associato.",
          heroImage: "/Pagina Info/Documenti F24.png",
          topics: [
            {
              title: "Convertitore Modelli F24",
              description:
                "Estrai automaticamente i dati e importali nel tuo gestionale con un semplice processo di conversione.",
              product: "f24",
              resources: [
                pdfResource("/static/pdf/Convertitore Modelli F24.pdf"),
                videoResource([
                  {
                    src: "/static/video/Conversione F24 - Completa.mp4",
                    text: "Come convertire un Modello F24 passo per passo",
                  },
                  {
                    src: "/static/video/Conversione F24 - Caso nessun cod tributo.mp4",
                    text: "Conversione con tutti i Codici Tributo già presenti",
                  },
                ]),
              ],
            },
            {
              title: "Libreria Codici Tributo",
              description:
                "Ricerca, filtra e trova velocemente il codice corretto per le tue operazioni fiscali.",
              library: "tributi",
              resources: [
                pdfResource("/static/pdf/Piano Codici Tributo.pdf"),
                videoResource([
                  {
                    src: "/static/video/Piano Codici Tributo - Leggi F24.mp4",
                    text: 'Aggiornare il Piano Codici Tributo con "Leggi Modello F24"',
                  },
                  {
                    src: "/static/video/Piano Codici Tributo - Aggiungi Riga.mp4",
                    text: 'Aggiornare il Piano Codici Tributo con "Aggiungi Riga"',
                  },
                  {
                    src: "/static/video/Piano Codici Tributo - Carica Piano.mp4",
                    text: 'Aggiornare il Piano Codici Tributo con "Carica Piano"',
                  },
                ]),
              ],
            },
          ],
        },
        {
          id: "bilancio",
          title: "Bilancio Analitico",
          product: "bilancio",
          description:
            "Gestisci la conversione dei bilanci analitici e la libreria condivisa con il tuo studio.",
          heroImage: "/Pagina Info/Documenti Bilancio Analitico.png",
          topics: [
            {
              title: "Convertitore Bilancio Analitico",
              description:
                "Estrai automaticamente i codici conto e salva un file elaborato per ogni cliente.",
              product: "bilancio",
              resources: [
                pdfResource("/static/pdf/Convertitore Bilancio Analitico.pdf"),
                videoResource([
                  {
                    src: "/static/video/Conversione Bilancio Analitico - Completa.mp4",
                    text: "Come convertire un Bilancio Analitico passo per passo",
                  },
                  {
                    src: "/static/video/Conversione Bilancio Analitico - Cod Conto Presenti.mp4",
                    text: "Conversione con tutti i Codici Conto già presenti nella libreria",
                  },
                ]),
              ],
            },
            {
              title: "Libreria Bilanci Analitici",
              description:
                "Gestisci i clienti dello studio e i relativi Piani dei Conti in una libreria condivisa.",
              library: "bilanci",
              resources: [
                pdfResource("/static/pdf/Libreria Clienti.pdf"),
                videoResource([
                  {
                    src: "/static/video/Libreria Bilancio Analitico.mp4",
                    text: "Come aggiornare il Piano dei Conti personale di un cliente",
                  },
                ]),
              ],
            },
          ],
        },
        {
          id: "store",
          title: "Store",
          icon: "fa-solid fa-cart-shopping",
          description:
            "Consulta coin, pacchetti disponibili e informazioni relative agli acquisti.",
          heroImage: "/Pagina Info/store.png",
          heroImageClass: "section-hero-illustration--compact",
          topics: [],
        },
        {
          id: "personale",
          title: "Area Personale",
          icon: "fa-solid fa-user",
          description:
            "Consulta i tuoi dati, le attività personali e le impostazioni del tuo account.",
          heroImage: "/Titoli pagine/Immagine Area personale.png",
          heroImageClass: "section-hero-illustration--compact",
          topics: [],
        },
        {
          id: "studio",
          title: "Il Mio Studio",
          icon: "fa-solid fa-users",
          description:
            "Gestisci clienti, utenti, documenti convertiti e configurazioni del tuo studio.",
          heroImage: "/Titoli pagine/Immagine Area Gestionale.png",
          heroImageClass: "section-hero-illustration--compact",
          topics: [],
        },
      ],
    };
  },
  computed: {
    activeSection() {
      return (
        this.sections.find((section) => section.id === this.activeSectionId) ||
        this.sections[0]
      );
    },
    activeSectionHasResources() {
      return this.activeSection.topics.some((topic) => topic.resources.length);
    },
    isIntroSection() {
      return this.activeSectionId === "intro";
    },
    introTopic() {
      return this.isIntroSection ? this.activeSection.topics[0] : null;
    },
    introResources() {
      return this.introTopic?.resources || [];
    },
    resourceCounts() {
      const resources = this.activeSection.topics.flatMap(
        (topic) => topic.resources
      );
      return {
        all: resources.length,
        documentation: resources.filter(
          (resource) => resource.type === "documentation"
        ).length,
        tutorial: resources.filter((resource) => resource.type === "tutorial")
          .length,
      };
    },
    filteredTopics() {
      const query = this.normalizeText(this.searchQuery);

      return this.activeSection.topics
        .map((topic) => {
          const topicMatches = this.normalizeText(
            `${topic.title} ${topic.description}`
          ).includes(query);
          const filteredResources = topic.resources.filter((resource) => {
            if (
              this.activeFilter !== "all" &&
              resource.type !== this.activeFilter
            ) {
              return false;
            }
            if (!query || topicMatches) return true;

            const videos = (resource.videos || [])
              .map((video) => video.text)
              .join(" ");
            return this.normalizeText(
              `${resource.title} ${resource.description} ${videos}`
            ).includes(query);
          });

          return { ...topic, filteredResources };
        })
        .filter((topic) => topic.filteredResources.length);
    },
  },
  mounted() {
    this.openDefaultIntroResource();
  },
  methods: {
    normalizeText(value) {
      return String(value || "")
        .toLocaleLowerCase("it-IT")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    selectSection(sectionId) {
      this.activeSectionId = sectionId;
      this.activeFilter = "all";
      this.searchQuery = "";
      this.closeResource();
      if (sectionId === "intro") this.openDefaultIntroResource();
    },
    setFilter(filter) {
      this.activeFilter = filter;
      if (this.activeResource && this.activeResource.type !== filter) {
        if (filter !== "all") this.closeResource();
      }
    },
    resourceKey(topic, resource) {
      return `${this.activeSectionId}-${topic.title}-${resource.type}`;
    },
    toggleResource(topic, resource) {
      const key = this.resourceKey(topic, resource);
      if (this.activeResource && this.activeResource.key === key) {
        if (this.isIntroSection) return;
        this.closeResource();
        return;
      }
      this.activeResource = {
        ...resource,
        key,
        topicTitle: topic.title,
      };
      this.activeVideoIndex = null;
    },
    openDefaultIntroResource() {
      const topic = this.introTopic;
      const resource = this.introResources.find(
        (item) => item.type === "tutorial"
      );
      if (topic && resource) this.toggleResource(topic, resource);
    },
    closeResource() {
      this.activeResource = null;
      this.activeVideoIndex = null;
    },
    toggleVideo(videoIndex) {
      this.activeVideoIndex =
        this.activeVideoIndex === videoIndex ? null : videoIndex;
    },
  },
};
</script>

<style scoped>
.info-page {
  display: flex;
  flex: 1 1 0;
  width: 100%;
  height: calc(100dvh - 7.65rem - var(--app-breadcrumb-height, 0rem));
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  background: #f7f9fb;
  color: #17243d;
}

.info-page-title {
  flex: 0 0 auto;
  width: auto;
  margin: 0.85rem clamp(1rem, 5vw, 6rem) 0;
}

.info-layout {
  display: grid;
  grid-template-columns: clamp(14.5rem, 17vw, 17rem) minmax(0, 1fr);
  flex: 1 1 0;
  width: 100%;
  height: 0;
  min-height: 0;
  gap: 1rem;
  padding: 0.85rem clamp(1rem, 5vw, 6rem) 0.75rem;
  box-sizing: border-box;
}

.info-navigation,
.info-content {
  min-height: 0;
  box-sizing: border-box;
  border: 1px solid #e0e7eb;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 37, 55, 0.06);
}

.info-navigation {
  display: flex;
  padding: 1.1rem 0 1rem;
  flex-direction: column;
  overflow: hidden;
}

.info-navigation h2 {
  margin: 0;
  padding: 0 1.25rem 0.85rem;
  color: #17243d;
  font-size: 1.08rem;
  text-align: left;
}

.info-navigation-list {
  display: flex;
  min-height: 0;
  padding: 0;
  flex-direction: column;
  overflow-y: auto;
}

.info-navigation-item {
  position: relative;
  display: grid;
  grid-template-columns: 1.8rem minmax(0, 1fr) auto;
  min-height: 3.2rem;
  padding: 0.65rem 1rem 0.65rem 1.25rem;
  align-items: center;
  gap: 0.65rem;
  border: 0;
  background: transparent;
  color: #425369;
  cursor: pointer;
  font: inherit;
  font-size: 0.86rem;
  text-align: left;
  transition: background 0.18s ease, color 0.18s ease;
}

.info-navigation-item::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 0.28rem;
  background: transparent;
}

.info-navigation-icon {
  display: inline-flex;
  width: 1.8rem;
  height: 1.8rem;
  align-items: center;
  justify-content: center;
  justify-self: center;
  background: transparent;
  color: #647386;
}

.info-navigation-icon > i {
  font-size: 1rem;
}

.info-navigation-logo {
  display: block;
  width: 1.3rem;
  height: 1.3rem;
  object-fit: contain;
}

.info-navigation-product-icon {
  width: 1.55rem;
  height: 1.55rem;
  transform: scale(1.04);
}

.info-navigation-item:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.info-navigation-item.active {
  background: linear-gradient(90deg, #edf1f4, #f6f7f8);
  color: #334155;
  font-weight: 700;
}

.info-navigation-item.active::before {
  background: #64748b;
}

.info-navigation-item.active > i {
  color: #526174;
}

.info-navigation-item.active .info-navigation-icon {
  background: transparent;
}

.navigation-chevron {
  font-size: 0.72rem;
}

.info-navigation-help {
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr);
  flex: 0 0 auto;
  margin: auto 1rem 0;
  padding: 1rem 0.2rem 0;
  gap: 0.65rem;
  border-top: 1px solid #e3e9ed;
  text-align: left;
}

.help-icon {
  display: inline-flex;
  width: 1.85rem;
  height: 1.85rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #637184;
  color: #ffffff;
  font-size: 0.8rem;
}

.info-navigation-help strong {
  display: block;
  color: #253852;
  font-size: 0.78rem;
  text-align: left;
}

.info-navigation-help p {
  margin: 0.25rem 0 0;
  color: #738194;
  font-size: 0.7rem;
  line-height: 1.45;
  text-align: left;
}

.info-content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  --info-accent: #08784f;
  --info-accent-strong: #126044;
  --info-accent-soft: #eaf7f0;
  --info-resource-from: #edf8f2;
  --info-resource-to: #f7fbf9;
  --info-resource-icon: #def1e7;
  --info-accent-border: #b7dcca;
  --info-accent-shadow: rgba(20, 91, 65, 0.08);
}

.info-content--f24 {
  --info-accent: #168bd2;
  --info-accent-strong: #075994;
  --info-accent-soft: #e8f4fc;
  --info-resource-from: #edf7fd;
  --info-resource-to: #f7fbfe;
  --info-resource-icon: #dceef9;
  --info-accent-border: #acd3ec;
  --info-accent-shadow: rgba(22, 139, 210, 0.1);
}

.info-content--bilancio {
  --info-accent: #b77909;
  --info-accent-strong: #795006;
  --info-accent-soft: #fcf2df;
  --info-resource-from: #fff6e7;
  --info-resource-to: #fffbf4;
  --info-resource-icon: #f8e7c3;
  --info-accent-border: #e8c98d;
  --info-accent-shadow: rgba(183, 121, 9, 0.1);
}

.section-hero {
  position: relative;
  display: grid;
  grid-template-columns: 4.4rem minmax(0, 1fr);
  flex: 0 0 auto;
  min-height: 6.5rem;
  padding: 1rem 13rem 0.9rem 1.5rem;
  align-items: center;
  gap: 1rem;
  overflow: hidden;
  box-sizing: border-box;
  border-bottom: 1px solid #e4eaed;
}

.section-hero--intro {
  grid-template-columns: 4.4rem minmax(0, 1fr) minmax(17rem, 20rem);
  min-height: 7.35rem;
  padding: 0.6rem 1.5rem;
}

.section-hero-icon {
  display: inline-flex;
  width: 4.1rem;
  height: 4.1rem;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(145deg, #fbfcfd, #f2f4f6);
  color: #5f6f82;
  font-size: 1.85rem;
}

.section-hero-icon img {
  display: block;
  width: 78%;
  height: 78%;
  object-fit: contain;
}

.section-hero-icon .product-icon {
  transform: scale(1.12);
}

.intro-resource-switcher {
  display: grid;
  grid-column: 3;
  width: 100%;
  gap: 0.45rem;
}

.intro-resource-card {
  grid-template-columns: 2rem minmax(0, 1fr) auto;
  min-height: 3.05rem;
  padding: 0.38rem 0.55rem;
  gap: 0.55rem;
}

.intro-resource-card .resource-icon {
  width: 2rem;
  height: 2rem;
}

@media (min-width: 1401px) {
  .section-hero--intro {
    grid-template-columns: 4.4rem minmax(18rem, 1fr) minmax(34rem, 38rem);
    min-height: 6.6rem;
  }

  .intro-resource-switcher {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
  }

  .intro-resource-card {
    min-height: 3.55rem;
  }
}

.section-hero-copy {
  position: relative;
  z-index: 2;
  min-width: 0;
  text-align: left;
}

.section-hero h2 {
  margin: 0;
  color: #17243d;
  font-size: 1.55rem;
  line-height: 1.12;
}

.section-hero p {
  max-width: 50rem;
  margin: 0.35rem 0 0;
  color: #6a7b8e;
  font-size: 0.82rem;
  line-height: 1.45;
}

.section-hero-illustration {
  position: absolute;
  top: 50%;
  right: 1.1rem;
  width: 11.5rem;
  max-height: 8.6rem;
  object-fit: contain;
  opacity: 0.72;
  transform: translateY(-50%);
  pointer-events: none;
}

.section-hero-illustration--compact {
  right: 1.5rem;
  width: 10rem;
  max-height: 7.7rem;
}

.info-controls {
  display: grid;
  grid-template-columns: minmax(16rem, 1fr) auto;
  flex: 0 0 auto;
  padding: 0.85rem 1.5rem;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid #e7ecef;
}

.info-search {
  display: flex;
  min-width: 0;
  min-height: 2.7rem;
  padding: 0 0.9rem;
  align-items: center;
  gap: 0.65rem;
  box-sizing: border-box;
  border: 1px solid #d8e1e7;
  border-radius: 9px;
  background: #ffffff;
  color: #6b7c90;
}

.info-search:focus-within {
  border-color: var(--info-accent-border);
  box-shadow: 0 0 0 3px var(--info-accent-shadow);
}

.info-search input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #253852;
  font: inherit;
  font-size: 0.8rem;
}

.info-search input::placeholder {
  color: #8a97a7;
}

.info-filters {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.info-filters button {
  display: inline-flex;
  min-height: 2.7rem;
  padding: 0.55rem 0.9rem;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border: 1px solid #d8e1e7;
  border-radius: 999px;
  background: #ffffff;
  color: #526174;
  cursor: pointer;
  font: inherit;
  font-size: 0.72rem;
  font-weight: 650;
  white-space: nowrap;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.info-filters button:hover {
  border-color: #c7d1da;
  background: #f3f5f7;
  color: #253852;
}

.info-filters button.active {
  border-color: #aebbc7;
  background: linear-gradient(135deg, #e9eef2, #dfe7ed);
  color: #334155;
}

.info-filters button:focus-visible {
  outline: 3px solid rgba(100, 116, 139, 0.18);
  outline-offset: 2px;
}

.info-results {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0.9rem 1.5rem 1.25rem;
  overflow-y: auto;
  scrollbar-color: #9ba9a3 transparent;
  scrollbar-width: thin;
}

.info-results--intro {
  padding-top: 0.65rem;
}

.topic-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.topic-card {
  padding: 0.95rem 1rem 1rem;
  border: 1px solid #dfe6eb;
  border-radius: 11px;
  background: #ffffff;
}

.topic-card--intro {
  padding: 0;
  border: 0;
  background: transparent;
}

.topic-heading {
  display: grid;
  grid-template-columns: 3.2rem minmax(0, 1fr);
  align-items: center;
  gap: 0.85rem;
  text-align: left;
}

.topic-icon {
  display: inline-flex;
  width: 3.2rem;
  height: 3.2rem;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: var(--info-accent-soft);
  color: var(--info-accent);
  font-size: 1.3rem;
}

.topic-icon .product-icon {
  width: 78%;
  height: 78%;
  transform: scale(1.06);
}

.topic-icon .library-icon {
  width: 84%;
  height: 84%;
  transform: scale(1.08);
}

.topic-icon .library-icon--bilanci {
  transform: scale(0.96);
}

.topic-heading h3 {
  margin: 0;
  color: var(--info-accent-strong);
  font-size: 1rem;
  line-height: 1.25;
}

.topic-heading p {
  margin: 0.28rem 0 0;
  color: #708094;
  font-size: 0.75rem;
  line-height: 1.45;
}

.topic-resources {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  margin: 0.75rem 0 0 4.05rem;
  gap: 0.7rem;
}

.resource-card {
  display: grid;
  grid-template-columns: 2.3rem minmax(0, 1fr) auto;
  min-height: 4rem;
  padding: 0.55rem 0.7rem;
  align-items: center;
  gap: 0.65rem;
  border: 1px solid transparent;
  border-radius: 9px;
  background: linear-gradient(
    135deg,
    var(--info-resource-from),
    var(--info-resource-to)
  );
  color: var(--info-accent-strong);
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
}

.resource-card:hover,
.resource-card.active {
  transform: translateY(-1px);
  border-color: var(--info-accent-border);
  box-shadow: 0 6px 14px var(--info-accent-shadow);
}

.resource-icon {
  display: inline-flex;
  width: 2.3rem;
  height: 2.3rem;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: var(--info-resource-icon);
  color: var(--info-accent);
  font-size: 1rem;
}

.resource-copy {
  min-width: 0;
}

.resource-copy strong,
.resource-copy small {
  display: block;
}

.resource-copy strong {
  color: var(--info-accent-strong);
  font-size: 0.82rem;
}

.resource-copy small {
  margin-top: 0.15rem;
  overflow: hidden;
  color: #758599;
  font-size: 0.67rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resource-chevron {
  color: var(--info-accent-strong);
  font-size: 0.72rem;
}

.intro-resource-card {
  border-color: #e0e6eb;
  background: linear-gradient(135deg, #eef2f5, #f8fafb);
  color: #263a50;
  box-shadow: none;
}

.intro-resource-card .resource-icon {
  background: #e1f1e9;
  color: #08784f;
}

.intro-resource-card .resource-copy strong {
  color: #20364c;
}

.intro-resource-card .resource-chevron {
  color: #08784f;
}

.intro-resource-card:hover {
  border-color: #c9d3dc;
  background: linear-gradient(135deg, #e9eef2, #f4f7f9);
  box-shadow: 0 5px 12px rgba(42, 59, 75, 0.08);
}

.intro-resource-card.active {
  border-color: #a7cfbc;
  background: linear-gradient(135deg, #edf5f1, #f7faf9);
  box-shadow: 0 5px 12px rgba(20, 91, 65, 0.09);
}

.resource-viewer {
  margin: 0.85rem 0 0 4.05rem;
  overflow: hidden;
  border: 1px solid #dce6e1;
  border-radius: 10px;
  background: #f8faf9;
}

.resource-viewer--intro {
  margin: 0;
}

.resource-viewer--intro .resource-pdf {
  height: min(38rem, 58vh);
}

.resource-viewer--intro .tutorial-video {
  max-height: min(34rem, 58vh);
}

.resource-viewer-heading {
  display: flex;
  min-height: 3rem;
  padding: 0.55rem 0.8rem;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  box-sizing: border-box;
  border-bottom: 1px solid #dfe7e3;
  background: #ffffff;
  text-align: left;
}

.resource-viewer-heading span,
.resource-viewer-heading strong {
  display: block;
}

.resource-viewer-heading span {
  color: #728195;
  font-size: 0.64rem;
  text-transform: uppercase;
}

.resource-viewer-heading strong {
  margin-top: 0.1rem;
  color: #18304a;
  font-size: 0.8rem;
}

.resource-viewer-heading button {
  display: inline-flex;
  width: 1.8rem;
  height: 1.8rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 1.8rem;
  border: 1px solid #dce4e0;
  border-radius: 999px;
  background: #ffffff;
  color: #687789;
  cursor: pointer;
}

.resource-pdf {
  display: block;
  width: 100%;
  height: min(31rem, 52vh);
  border: 0;
  background: #eef2f1;
}

.tutorial-list {
  display: flex;
  padding: 0.7rem;
  flex-direction: column;
  gap: 0.55rem;
}

.tutorial-item {
  overflow: hidden;
  border: 1px solid #e0e7e4;
  border-radius: 8px;
  background: #ffffff;
}

.tutorial-trigger {
  display: grid;
  grid-template-columns: 1.8rem minmax(0, 1fr) auto;
  width: 100%;
  min-height: 2.8rem;
  padding: 0.55rem 0.75rem;
  align-items: center;
  gap: 0.55rem;
  border: 0;
  background: #ffffff;
  color: #263b4b;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.tutorial-trigger > span {
  color: #16825d;
  font-size: 1.05rem;
  text-align: center;
}

.tutorial-trigger strong {
  font-size: 0.75rem;
  line-height: 1.35;
}

.tutorial-trigger > i {
  color: #728195;
  font-size: 0.68rem;
}

.tutorial-video {
  display: block;
  width: 100%;
  max-height: 30rem;
  background: #000000;
}

.no-search-results {
  display: flex;
  min-height: 15rem;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  color: #83909e;
  text-align: center;
}

.no-search-results i {
  font-size: 1.35rem;
}

.no-search-results p {
  margin: 0.65rem 0 0;
  font-size: 0.8rem;
}

.empty-section-message {
  display: flex;
  flex: 1 1 auto;
  min-height: 12rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.8rem;
  background: #ffffff;
  color: #7b8796;
  text-align: center;
}

.empty-section-message span {
  display: inline-flex;
  width: 3.2rem;
  height: 3.2rem;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f1f3f5;
  color: #84909d;
  font-size: 1.3rem;
}

.empty-section-message p {
  margin: 0;
  font-size: 0.96rem;
  font-weight: 700;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1600px), (max-height: 900px) {
  .resource-viewer--intro .resource-viewer-heading {
    display: none;
  }
}

@media (min-width: 981px) and (max-height: 900px) {
  .info-page-title {
    margin-top: 0.6rem;
  }

  .info-layout {
    gap: 0.75rem;
    padding-top: 0.65rem;
  }
}

@media (max-width: 980px) {
  .info-page {
    height: auto;
    min-height: calc(100vh - 6rem);
    overflow: visible;
  }

  .info-layout {
    grid-template-columns: 1fr;
    height: auto;
    padding-inline: 1rem;
  }

  .info-page-title {
    margin-right: 1rem;
    margin-left: 1rem;
  }

  .info-navigation {
    min-height: auto;
  }

  .info-navigation-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    overflow: visible;
  }

  .info-navigation-help {
    display: none;
  }

  .info-content {
    min-height: 36rem;
  }

  .section-hero--intro {
    grid-template-columns: 4.4rem minmax(0, 1fr);
    padding-right: 1.5rem;
  }

  .intro-resource-switcher {
    grid-column: 2;
  }

  .info-controls {
    grid-template-columns: 1fr;
  }

  .info-filters {
    flex-wrap: wrap;
  }
}

@media (max-width: 640px) {
  .section-hero {
    grid-template-columns: 3.4rem minmax(0, 1fr);
    padding-right: 1rem;
  }

  .section-hero-icon {
    width: 3.2rem;
    height: 3.2rem;
    font-size: 1.35rem;
  }

  .section-hero--intro {
    grid-template-columns: 3.4rem minmax(0, 1fr);
  }

  .intro-resource-switcher {
    grid-column: 1 / -1;
  }

  .section-hero-illustration {
    display: none;
  }

  .info-controls,
  .info-results {
    padding-right: 0.8rem;
    padding-left: 0.8rem;
  }

  .topic-resources,
  .resource-viewer {
    margin-left: 0;
  }

  .topic-resources {
    grid-template-columns: 1fr;
  }
}
</style>
